import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import useQuery from 'hooks/useQuery';
import { getNestedCategory } from 'domain/categories/helpers';
import { workspaceDoctypesSelector, workspaceSubtypesSelector, subtypeFilterSelector } from 'domain/documents';
import { documentWorkSpaceTypeSelector } from 'domain/companies/companiesSelector';
import { useDocumentsAggregations } from 'domain/documents/queries';
import type { SearchDoctype, SearchSubtypeChip } from 'domain/documents/schemas';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';

// Route-derived inputs for documents/aggregations — mirrors the grid-search global filter
// (useGridContext) minus filterModel: companyId, active category, confidential flag, the URL global
// filter (date/text/query), and the active sub-type as subtags[] (forwarded only to pin its chip,
// not to narrow counts).
const useWorkspaceAggregationsParams = () => {
  const params = useParams();
  const query = useQuery();
  const subtype = useSelector(subtypeFilterSelector);
  const { companyType } = useContext(CompanyWorkspaceContext);
  return {
    companyId: params.companyId ?? '',
    category: getNestedCategory(params) ?? '',
    isConfidential: companyType === 'confidential',
    query,
    // sent as subtags[] so the backend pins the active sub-type chip at zero results (DA-19691).
    subtype: subtype ?? null,
  };
};

type WorkspaceCategorization = {
  doctypes: SearchDoctype[];
  subtypes: SearchSubtypeChip[];
  // Only ever true on grid: tile's block rides the saga-owned search_new response and has no
  // separate loading flag. Consumers use it to tell "not fetched yet" (hold the bar with a
  // skeleton) apart from "this category has no tabs" (backend sent null — render nothing).
  isLoading: boolean;
};

// Source-agnostic read of the workspace categorization block (doctype tabs + Inbox sub-type chips).
// Tile reads Redux (aggregations ride the saga-owned search_new poll, kept fresh by the worker);
// grid reads TanStack (documents/aggregations, its own endpoint — grid has no worker). No
// TanStack→Redux sync; the grid query is enabled only in grid view, so tile makes no extra request.
//
// Tabs and chips are returned by ONE hook on purpose: they come from the same response, so two
// separate accessors meant two query observers on the same key. That was harmless while both merely
// read `data`, but with placeholderData (DA-19731) the two observers could resolve to different
// values and render tabs and chips from different snapshots.
// Reads the view through the SHARED selector on purpose, even though it lags a company switch by the
// companyLogin round trip (it keys env.workSpace by documents.companyId). A URL-keyed read here would
// desync from WorkspaceViewToggle, which sits in the same bar and both reads AND WRITES through the
// Redux id — mid-switch the bar would follow company B while the toggle still wrote company A's
// preference. The route saga fixes its own copy of this lag with params.companyId (it only reads);
// here the cost of the lag is one render on a cold load, which the skeleton already covers.
export const useWorkspaceCategorization = (): WorkspaceCategorization => {
  const isGrid = useSelector(documentWorkSpaceTypeSelector) === 'grid';
  const tileTabs = useSelector(workspaceDoctypesSelector);
  const tileSubtypes = useSelector(workspaceSubtypesSelector);
  const params = useWorkspaceAggregationsParams();

  // Offer the tile block as a placeholder ONLY when it actually holds something. Passing empty
  // arrays would resolve the query to `status: 'success'` with zero tabs, which reads as "this
  // category has no tabs" and would suppress the skeleton on a genuinely cold grid load.
  const placeholder = tileTabs.length ? { tabs: tileTabs, subtypes: tileSubtypes } : undefined;

  const { tabs, subtypes, isLoading } = useDocumentsAggregations(params, isGrid, placeholder);

  return isGrid
    ? { doctypes: tabs, subtypes, isLoading }
    : { doctypes: tileTabs, subtypes: tileSubtypes, isLoading: false };
};
